<script>
    import {bxSubmitReview} from "../Methods.svelte";
    import {form} from "../stores/store";
    import Rating from "./Rating.svelte";
    import FileDropzone from "./FileDropzone.svelte";
    import Captcha from "./Captcha.svelte";

    const POST_MAX = 2000;
    const EMAIL_RE = /.+@.+\..+/;

    let formElement;

    let name = '';
    let email = '';
    let rating = 0;
    let post = '';
    let files = [];
    let consent = false;
    let captchaToken = '';

    let errors = {
        name: '',
        email: '',
        rating: '',
        post: '',
        files: '',
        consent: '',
        captcha: '',
        common: ''
    };

    let isSubmitting = false;

    $: postLeft = POST_MAX - post.length;

    function validate() {
        errors = {
            name: '',
            email: '',
            rating: '',
            post: '',
            files: errors.files,
            consent: '',
            captcha: '',
            common: ''
        };

        const dummyInput = formElement?.querySelector('#dummy');
        if (dummyInput && dummyInput.value !== '') {
            errors.common = 'Bot alert';
            return false;
        }

        if (!name.trim()) {
            errors.name = 'Заполните поле';
        }

        const emailTrim = email.trim();
        if (!emailTrim) {
            errors.email = 'Заполните поле';
        } else if (!EMAIL_RE.test(emailTrim)) {
            errors.email = 'Введите корректный email';
        }

        if (rating < 1 || rating > 5) {
            errors.rating = 'Поставьте оценку от 1 до 5';
        }

        const postTrim = post.trim();
        if (!postTrim) {
            errors.post = 'Заполните поле';
        } else if (postTrim.length > POST_MAX) {
            errors.post = `Максимум ${POST_MAX} символов`;
        }

        // files validity already enforced by FileDropzone — leave any inherited error in place

        if (!consent) {
            errors.consent = 'Подтвердите согласие';
        }

        if (!captchaToken) {
            errors.captcha = 'Пройдите проверку «Я не робот»';
        }

        errors = errors;

        return !errors.name
            && !errors.email
            && !errors.rating
            && !errors.post
            && !errors.files
            && !errors.consent
            && !errors.captcha
            && !errors.common;
    }

    async function submit() {
        if (isSubmitting) return;

        if (!validate()) {
            return;
        }

        isSubmitting = true;
        errors.common = '';

        const fd = new FormData();
        fd.append('dummy', '');

        ($form.defaultData || []).forEach(({code, value}) => {
            fd.append(code, value);
        });

        fd.append('NAME', name.trim());
        fd.append('EMAIL', email.trim());
        fd.append('RATING', String(rating));
        fd.append('POST', post.trim());
        fd.append('PRIVACY_CONSENT', 'Y');
        fd.append('smart-token', captchaToken);

        files.forEach(f => fd.append('FILE[]', f));

        const result = await bxSubmitReview(fd);

        isSubmitting = false;

        if (result.success) {
            form.update(s => ({...s, success: true}));
        } else {
            errors.common = result.error || 'Не удалось отправить. Попробуйте ещё раз.';
            errors = errors;
        }
    }

    function clearError(key) {
        if (errors[key]) {
            errors[key] = '';
            errors = errors;
        }
    }

    function onCaptchaToken(event) {
        captchaToken = event.detail || '';
        clearError('captcha');
    }

    function onFilesError(event) {
        errors.files = event.detail || '';
        errors = errors;
    }
</script>

<form bind:this={formElement}
      class="review-form"
      on:submit|preventDefault|stopPropagation={submit}
      action="javascript:void(0)"
      novalidate>

    <input type="text" id="dummy" name="dummy" value="" class="visually-hidden" tabindex="-1" autocomplete="off">

    <div class="field">
        <label for="rf-name">Имя <span class="required">*</span></label>
        <input id="rf-name"
               type="text"
               name="NAME"
               autocomplete="name"
               bind:value={name}
               class:has-error={errors.name}
               on:input={() => clearError('name')}/>
        {#if errors.name}<span class="field-error">{errors.name}</span>{/if}
    </div>

    <div class="field">
        <label for="rf-email">Email <span class="required">*</span></label>
        <input id="rf-email"
               type="email"
               name="EMAIL"
               autocomplete="email"
               bind:value={email}
               class:has-error={errors.email}
               on:input={() => clearError('email')}/>
        {#if errors.email}<span class="field-error">{errors.email}</span>{/if}
    </div>

    <div class="field">
        <span class="field-label">Оценка <span class="required">*</span></span>
        <Rating bind:value={rating} on:change={() => clearError('rating')}/>
        {#if errors.rating}<span class="field-error">{errors.rating}</span>{/if}
    </div>

    <div class="field">
        <span class="field-label">Фотографии</span>
        <FileDropzone bind:files error={errors.files} on:error={onFilesError}/>
        {#if errors.files}<span class="field-error">{errors.files}</span>{/if}
    </div>

    <div class="field">
        <label for="rf-post">Отзыв <span class="required">*</span></label>
        <textarea id="rf-post"
                  name="POST"
                  rows="5"
                  maxlength={POST_MAX}
                  bind:value={post}
                  class:has-error={errors.post}
                  on:input={() => clearError('post')}></textarea>
        <div class="counter" class:counter--warn={postLeft < 100}>{post.length} / {POST_MAX}</div>
        {#if errors.post}<span class="field-error">{errors.post}</span>{/if}
    </div>

    <div class="field">
        <Captcha sitekey={$form.captchaSitekey} bind:token={captchaToken} on:token={onCaptchaToken}/>
        {#if errors.captcha}<span class="field-error">{errors.captcha}</span>{/if}
    </div>

    <div class="field">
        <label class="consent">
            <input type="checkbox"
                   bind:checked={consent}
                   on:change={() => clearError('consent')}>
            <span>
                Я даю согласие на обработку моих персональных данных в соответствии с
                <a href="/o-nas/politika-obrabotki-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer">Политикой обработки персональных данных</a>
                и даю
                <a href="/o-nas/soglasie-na-obrabotku-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer">Согласие на обработку персональных данных</a>
            </span>
        </label>
        {#if errors.consent}<span class="field-error">{errors.consent}</span>{/if}
    </div>

    {#if errors.common}<div class="field-error common-error">{errors.common}</div>{/if}

    <button type="submit"
            class="button button-primary form-submit"
            disabled={isSubmitting || !consent}>
        {isSubmitting ? 'Отправка…' : 'Отправить отзыв'}
    </button>
</form>

<style>
    .review-form {
        display: flex;
        flex-direction: column;
        gap: 14px;
        max-width: 600px;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
    }

    .field label,
    .field .field-label {
        font-size: 13px;
        font-weight: 500;
        color: #333;
    }

    .field input[type="text"],
    .field input[type="email"],
    .field textarea {
        width: 100%;
        font: inherit;
        padding: 9px 12px;
        border: 1px solid #d0d0d0;
        border-radius: 6px;
        background: #fff;
        color: #1a1a1a;
        transition: border-color 120ms ease, box-shadow 120ms ease;
        box-sizing: border-box;
    }

    .field input[type="text"]:focus,
    .field input[type="email"]:focus,
    .field textarea:focus {
        outline: none;
        border-color: var(--main-color, #a06840);
        box-shadow: 0 0 0 3px rgba(160, 104, 64, 0.15);
    }

    .field textarea {
        resize: vertical;
        min-height: 110px;
    }

    .has-error {
        border-color: var(--red, #d0021b) !important;
    }

    .required {
        color: var(--red, #d0021b);
    }

    .field-error {
        color: var(--red, #d0021b);
        font-size: 12px;
        line-height: 1.3;
        display: block;
    }

    .common-error {
        padding: 10px 12px;
        background: #fff0f1;
        border: 1px solid #f4c0c5;
        border-radius: 6px;
    }

    .counter {
        font-size: 11px;
        color: #888;
        text-align: right;
    }

    .counter--warn {
        color: var(--red, #d0021b);
    }

    .consent {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-weight: 400;
        font-size: 13px;
        color: #333;
        line-height: 1.4;
    }

    .consent input[type="checkbox"] {
        margin-top: 3px;
        accent-color: var(--main-color, #a06840);
    }

    .form-submit {
        align-self: center;
        margin-top: 8px;
        padding: 11px 30px 10px;
        font-family: Ubuntu, Arial, Helvetica, sans-serif;
        font-size: 11px;
        font-weight: 500;
        line-height: 16px;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        background: var(--main-color, #a06840);
        color: #fff;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: opacity 120ms ease, transform 120ms ease;
    }

    .form-submit:hover:not(:disabled) {
        opacity: 0.92;
    }

    .form-submit:disabled {
        opacity: 0.6;
        cursor: progress;
    }

    .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    @media (max-width: 600px) {
        .review-form {
            max-width: 100%;
        }
    }
</style>
